import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, FlaskConical, ArrowLeft, CheckCircle2 } from "lucide-react";
import { formatAuthError } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(formatAuthError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="forgot-password-page"
      className="min-h-[calc(100vh-7rem)] bg-cream-200/40 py-14 md:py-20"
    >
      <div className="container-x max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center mb-5">
            <FlaskConical className="w-5 h-5 text-sage-600" strokeWidth={1.8} />
          </div>
          <p className="overline mb-2">Account Recovery</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-charcoal-600 leading-tight">
            Forgot password
          </h1>
          <p className="mt-3 text-sm text-charcoal-400">
            Enter your email and we&apos;ll send you a secure link to set a new password.
          </p>
        </div>

        {sent ? (
          <div
            data-testid="forgot-password-sent"
            className="surface-card p-8 md:p-10 text-center space-y-5"
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-sage-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-sage-700" strokeWidth={1.8} />
            </div>
            <h2 className="font-display text-2xl font-bold text-charcoal-600">
              Check your inbox
            </h2>
            <p className="text-sm text-charcoal-400 leading-relaxed">
              If an account exists for <strong className="text-charcoal-600">{email}</strong>,
              we've sent a password reset link. It's valid for 30 minutes and can only be used once.
            </p>
            <p className="text-xs text-charcoal-400">
              Didn't get anything? Check your spam folder, or{" "}
              <button
                type="button"
                data-testid="forgot-password-resend"
                onClick={() => setSent(false)}
                className="text-sage-700 hover:text-sage-800 underline underline-offset-4"
              >
                try a different email
              </button>
              .
            </p>
            <Link
              to="/login"
              data-testid="forgot-password-back-to-login"
              className="inline-flex items-center justify-center text-sm text-sage-700 hover:text-sage-800 font-medium"
            >
              <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={2} />
              Back to sign in
            </Link>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="surface-card p-8 md:p-10 space-y-5"
            data-testid="forgot-password-form"
          >
            <label htmlFor="email" className="block">
              <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">
                Email
              </span>
              <input
                id="email"
                data-testid="forgot-password-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@lab.edu"
              />
            </label>

            {error && (
              <p
                data-testid="forgot-password-error"
                className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="forgot-password-submit"
              disabled={submitting}
              className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Mail className="w-4 h-4 mr-2" strokeWidth={2} />
              {submitting ? "Sending…" : "Send reset link"}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                data-testid="forgot-password-back-link"
                className="inline-flex items-center text-sm text-charcoal-400 hover:text-sage-700"
              >
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={2} />
                Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
